import { useState, useEffect, useCallback } from 'react';
import api from '../api/apiHelper';
import { persistDashboardData, invalidateApiCache } from './apiCache';

export const DOMAIN_PP = 'PP';
export const DOMAIN_PR = 'PR';

// Etykiety poziomu — jedno zrodlo dla naglowka, ustawien i modala celow.
export const DOMAIN_LABELS = {
    [DOMAIN_PP]: { short: 'PP', label: 'Podstawa', full: 'Matura Podstawowa' },
    [DOMAIN_PR]: { short: 'PR', label: 'Rozszerzenie', full: 'Matura Rozszerzona' },
};

const readActiveDomain = () => {
    try {
        const stored = localStorage.getItem('userStatus');
        return JSON.parse(stored)?.active_domain || DOMAIN_PP;
    } catch {
        return DOMAIN_PP;
    }
};

// Cache zalezne od aktywnej domeny (PP/PR) — po przelaczeniu trzeba je odswiezyc,
// zeby ekrany dociagnely dane dla nowej domeny. surefire-atoms (niepewniaki)
// swiadomie pomijamy — nie sa filtrowane po domenie.
const DOMAIN_SCOPED_KEYS = [
    'fields_preferences',
    'training_basket_details',
    'exams',
    'user_stats',
];

// Przelacza aktywna domene globalnie: zapisuje wybor na backendzie, odswieza
// dashboard na zywo (persistDashboardData -> UserDashboardContext) i unieważnia
// cache pozostalych ekranow zaleznych od domeny. Zwraca swieze dane dashboardu.
export async function switchActiveDomain(domain) {
    await api.post('update_active_domain/', { active_domain: domain });
    const { data } = await api.get('user_dashboard_data/', { skipLoading: true });
    persistDashboardData(data);
    DOMAIN_SCOPED_KEYS.forEach((key) => invalidateApiCache(key));
    return data;
}

// Przelaczenie poziomu podmienia tresc calej aplikacji — dzialy, arkusze, statystyki,
// cele. Komunikaty ida po kolei (startAtFirst), bo opowiadaja co sie faktycznie dzieje.
const SWITCH_MESSAGES = {
    [DOMAIN_PP]: [
        'wracamy na maturę podstawową',
        'ładujemy działy podstawy',
        'podmieniamy arkusze na te z podstawy',
        'wracamy do Pewniaków',
        'ustawiamy cel dla podstawy',
    ],
    [DOMAIN_PR]: [
        'przełączamy Cię na maturę rozszerzoną',
        'ładujemy działy rozszerzenia',
        'podmieniamy arkusze na te z rozszerzenia',
        'podmieniamy Pewniaki na Niepewniaki',
        'ustawiamy cel dla rozszerzenia',
    ],
};

const SWITCH_HINT = 'To może chwilę potrwać - przestawiamy całą aplikację na nowy poziom.';

/**
 * Przelaczanie poziomu z pelnoekranowym overlayem (SessionSetupPopup).
 * Zwraca `start(domain)` oraz gotowe propsy do wstrzykniecia w overlay —
 * jedna logika dla obu miejsc, w ktorych da sie zmienic poziom.
 */
export function useDomainSwitch() {
    const [targetDomain, setTargetDomain] = useState(null);
    const [isReady, setIsReady] = useState(false);

    const start = useCallback(async (domain) => {
        setTargetDomain(domain);
        setIsReady(false);
        try {
            await switchActiveDomain(domain);
            setIsReady(true);
        } catch (error) {
            setTargetDomain(null);
            throw error;
        }
    }, []);

    const overlayProps = {
        isOpen: targetDomain !== null,
        isDataReady: isReady,
        startAtFirst: true,
        hint: SWITCH_HINT,
        messages: SWITCH_MESSAGES[targetDomain] || SWITCH_MESSAGES[DOMAIN_PP],
        onComplete: () => {
            setTargetDomain(null);
            setIsReady(false);
        },
    };

    return { start, overlayProps, isSwitching: targetDomain !== null };
}

/**
 * Aktywny poziom (PP/PR) do wyswietlenia w chrome aplikacji.
 * Nasluchuje `dashboard-data-updated`, wiec naglowek przelacza sie od razu
 * po switchActiveDomain — bez remountu ekranu.
 */
export function useActiveDomain() {
    const [domain, setDomain] = useState(readActiveDomain);

    useEffect(() => {
        const handler = (e) => {
            const next = e.detail?.data?.account_status?.active_domain;
            if (next) setDomain(next);
        };
        window.addEventListener('dashboard-data-updated', handler);
        // Zsynchronizuj po zamontowaniu — userStatus mogl sie zmienic wczesniej.
        setDomain(readActiveDomain());
        return () => window.removeEventListener('dashboard-data-updated', handler);
    }, []);

    return domain;
}
